import { sub, isInBbox, distanceToEntities, getTangentNormalAtParametricPoint, BoundingBox, RawEntity, Arcified, Vector, Contour, Entity } from '@oshcut/oshlib'
import { PartType } from 'types'
import { findPart } from './findPart'
import { isSheetMetal } from 'partTypeGuards'

type ContourPartial<TEntity extends RawEntity> = { id: number, bbox?: BoundingBox, entities: TEntity[] }
type ArcifiedPartial<TContour extends ContourPartial<TEntity>, TEntity extends RawEntity> = {
  bbox?: BoundingBox
  contours: TContour[]
}
type PartCoord = { partId: string, rotation: number, position: Vector }

/**
 * For all partCoords, finds the contour closest to the given point.
 * @param p The point to test
 * @param parts Array of parts
 * @param partCoords Array of partCoords
 * @param maxDist If specified, contours are only registered if they are less than this distance away from point `p` (default = 0, no maximum distance)
 * @param filterContours An array of objects, each containing two keys: partId and contourId. If supplied, will only search contours matching the specified ids. Default = null, search all contours)
 * @param greedy If true, will stop the search when the first contour is found that meets all criteria. (default = false)
 * @param filterFn A function that is used to determine whether a particular contour should be tested. It is called with two arguments: the part the contour belongs to, and the contour itself.
 * @returns Information about the closest contour and the point on the contour closest to the given point `p`.
 */
export function contourHitTest(p: Vector, parts: PartType[], partCoords: PartCoord[], maxDist = 0, filterContours?: { partId: string, contourId: number }[], greedy = false, filterFn?: (part: Arcified, contour: Contour) => boolean) {

  // Log.info('contourHitTest(', p, parts, partCoords, maxDist, filterContours, greedy)

  function performTest(pc: PartCoord) {
    let part = findPart(parts, pc)
    if (!isSheetMetal(part)) return null
    let cos = Math.cos(pc.rotation)
    let sin = Math.sin(pc.rotation)
    // Rotate and translate to get p in original part coords
    let transP = sub(p, pc.position)
    transP = {
      x: cos * transP.x + sin * transP.y,
      y: -sin * transP.x + cos * transP.y
    }

    let filterContourIds = null
    if (filterContours) {
      filterContourIds = filterContours
        .filter(fc => fc.partId === pc.partId)
        .map(fc => fc.contourId)
    }
    return pointContourHitTest<Arcified, Contour, Entity>(transP, part.arcified, maxDist, filterContourIds, greedy, filterFn)
  }

  let bestResult = null
  let bestDistance = Infinity
  for (let pc of partCoords) {
    if (filterContours && !filterContours.some(fc => fc.partId === pc.partId)) {
      // Filter is specified, and this part is not included
      continue
    }
    let result = performTest(pc)

    if (result && maxDist > 0 && result.distance > maxDist) {
      // Result is not less than the maximum distance
      continue
    }

    if (result && result.distance < bestDistance) {
      let { tangent, normal } = getTangentNormalAtParametricPoint(result.contour, result.nearestEntity, result.nearestParametric)
      bestResult = { ...result, pc, tangent, normal }
      bestDistance = result.distance
      if (greedy) {
        // Found an OK result, return early
        break
      }
    }
  }

  return bestResult

}

/**
 * For a point and a part, finds a contour that is within minDist of the point.
 * @param point The point to test
 * @param part The arcified object
* @param maxDist If specified, contours are only hit if they are less than this distance away from point `p` (default = 0, no maximum distance)
 * @param filterContourIds An array of numbers specifying the contourIds that will be searched. (Default = null, search all contours)
 * @param greedy If true, will stop the search when the first contour is found that meets all criteria. (default = false)
 * @param filterFn A function that is used to determine whether a particular contour should be tested. It is called with two arguments: the part the contour belongs to, and the contour itself.
 * @returns Information about the closest contour and the point on the contour closest to the given point `p`.
 */
export function pointContourHitTest<
  // These generic types are so that we can use this function with "fake" parts that we create with limited properties,
  // yet still receieve the full types in the returned value when using it with complete parts.
  TArcified extends ArcifiedPartial<TContour, TEntity>,
  TContour extends ContourPartial<TEntity>,
  TEntity extends RawEntity
>(
  point: Vector,
  part: TArcified,
  maxDist = 0,
  filterContourIds: number[] | null = null,
  greedy = false,
  filterFn?: (part: TArcified, contour: TContour) => boolean
) {
  if (!part) return null
  if (maxDist > 0) {
    if (!part.bbox) throw new Error('part.bbox is required when maxDist is specified')
    let bboxWithMargin = {
      min: {
        x: part.bbox.min.x - maxDist,
        y: part.bbox.min.y - maxDist
      },
      max: {
        x: part.bbox.max.x + maxDist,
        y: part.bbox.max.y + maxDist
      }
    }

    if (!isInBbox(point, bboxWithMargin)) {
      return null
    }
  }

  let bestDist = Infinity
  let bestResult = null
  for (let c of part.contours) {
    if (filterContourIds && !filterContourIds.includes(c.id)) {
      // This contour is not included in the filtered list
      continue
    }
    if (filterFn && !filterFn(part, c)) {
      // Filter function returned false for this part and contour
      continue
    }

    if (maxDist > 0) {
      if (!c.bbox) throw new Error('contours must have a bbox when maxDist is specified')
      let contourBboxWithMargin = {
        min: {
          x: c.bbox.min.x - maxDist,
          y: c.bbox.min.y - maxDist
        },
        max: {
          x: c.bbox.max.x + maxDist,
          y: c.bbox.max.y + maxDist
        }
      }

      if (!isInBbox(point, contourBboxWithMargin)) {
        // This contour is too far away
        continue
      }
    }

    let result = distanceToEntities(point, c.entities, true)

    if (maxDist > 0 && result && result.distance > maxDist) {
      // Result is not less than the maximum distance
      continue
    }

    if (result && result.distance < bestDist) {
      // Found a better result
      bestResult = { ...result, contour: c }
      bestDist = result.distance
      if (greedy) {
        // Found an OK result, return early
        break
      }
    }

  }

  return bestResult
}